import _ from "lodash";
import { action, autorun, computed, flow, observable } from "mobx";
import { observer } from "mobx-react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import React, { useContext } from "react";
import { QueryClient } from "react-query";
import styled from "styled-components";
import { getLocStore, LocStoreInstance } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import { Store2 } from "../../../../WSUtils/Store";
import { Overwrite } from "../../../../WSUtils/TypeScriptUtils";
import { addUnsavedChange } from "../../../../WSUtils/unsavedChanges";
import { redirect } from "../../../../WSUtils/urlUtils";
import { UserInfo } from "../../../../WSUtils/UserInfo";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { concatClassnames } from "../../../../WSUtils/util";
import { WSFormGrid } from "../../../../WSUtils/WSFormGrid";
import {
  CVPSearchParams,
  queryStringStore,
} from "../../../../WSUtils/wsQueryStringStore";
import { CVEditStepType } from "../../../Steps/Step";
import { CVEditContextStore } from "../../CVEditContext";
import { CVEditStoreParams } from "../../CVEditStore";
import {
  CVEditBaseField,
  cveField,
  cveFieldStore,
  OnRenderCvEditComponent,
  OnRenderCVEditComponentProps,
} from "../../Fields/CVEditBaseField";

export interface CVEditDocumentEditorProps {
  openDocumentPicker: () => any;
  docEditorStore: CVEditDocumentEditorStore;
  headerText: string;
}
export const CVEditDocumentEditor = ({
  openDocumentPicker,
  docEditorStore,
  headerText,
}: CVEditDocumentEditorProps) => {
  const userInfo = useContext(UserInfoContext);

  return (
    <CVEditDocumentEditorStyles
      margin="1.15rem"
      float
      className="cve-document-editor cve-x-editor"
    >
      <div className="cvp-h3 cve-smaller-header">
        {headerText}

        <Button
          icon="pi pi-times"
          className={concatClassnames([
            "p-button-text p-button-icon-only p-button-rounded",
            "cve-editor-cancel",
          ])}
          onClick={flow(function* (evt) {
            evt.preventDefault();

            docEditorStore.closeEditor();

            return false;
          })}
        />
      </div>

      <CVEditBaseField
        def={docEditorStore.fields.fileSelectionField}
        openDocumentPicker={openDocumentPicker}
      />
      <CVEditBaseField def={docEditorStore.fields.documentNameField} />
    </CVEditDocumentEditorStyles>
  );
};

const CVEditDocumentEditorStyles = styled(WSFormGrid)`
  &.cve-document-editor {
  }
`;

export interface CVEditDocumentEditorStoreParams extends CVEditStoreParams {
  showEditor: () => boolean;
  getFile: () => File;
  onCloseEditor: () => any;
}

export class CVEditDocumentEditorStore extends Store2<any> {
  declare userInfo: UserInfo;
  declare queryClient: QueryClient;
  declare cvEditContext: CVEditContextStore;

  declare showEditor: () => boolean;
  declare getFile: () => File;
  declare onCloseEditor: () => any;

  constructor(params: CVEditDocumentEditorStoreParams) {
    super(params);

    const needsConfirmationBeforeLeaving = () => {
      const isViewingThis =
        queryStringStore.qs[CVPSearchParams.cvpad] === "new" &&
        this.cvEditContext.currentStep.value === CVEditStepType.Documents &&
        this._showEditor;

      if (!isViewingThis) return false;
      if (this.isTerminated) return false;
      return true;
    };

    this.dt.disposers.push(
      addUnsavedChange({
        needsConfirmationBeforeUnload: needsConfirmationBeforeLeaving,
        needsConfirmationBeforeSpaRedirect: needsConfirmationBeforeLeaving,
      })
    );
  }

  @computed get L() {
    return getLocStore(this.userInfo, LocContext.CV);
  }

  @computed get _showEditor() {
    return this.showEditor();
  }

  @computed get _file() {
    return this.getFile();
  }

  @action closeEditor = () => {
    this.isTerminated = true;
    redirect(null, {
      userInfo: this.userInfo,
      mergeQs: { [CVPSearchParams.cvpad]: null },
    });

    this.onCloseEditor?.();
  };

  @observable isTerminated = false;
  @observable hasAttemptedToClose = false;

  @computed get fields() {
    return {
      fileSelectionField: this.fileSelectionField,
      documentNameField: this.documentNameField,
    };
  }

  @computed get validationErrors() {
    return Object.values(this.fields).filter((f) => f.store.validationError);
  }

  @computed get visibleValidationErrors() {
    return Object.values(this.fields).filter(
      (f) => f.store.visibleValidationError
    );
  }

  @observable fileSelectionField = cveField(() => {
    const store = cveFieldStore({
      value: null,
      get validationError(): string {
        return "";
      },
      get showValidationError(): boolean {
        return false;
      },
    });

    type OnRenderFileSelectionFieldComponentProps = Overwrite<
      OnRenderCVEditComponentProps,
      {
        openDocumentPicker: any;
      }
    >;

    const onRenderComponent = ({
      def,
      openDocumentPicker,
    }: OnRenderFileSelectionFieldComponentProps) => {
      const file = this._file;

      return (
        <div className="p-inputgroup">
          <InputText
            value={file?.name}
            disabled
            title={this.L.id("9e43d1026e36").loc("Valgt fil")}
          />
          <Button
            icon="pi pi-chevron-down"
            className="p-button-icon-only p-button-secondary"
            onClick={(evt) => {
              evt.preventDefault();
              openDocumentPicker();
              return false;
            }}
            title={this.L.id("ba3cb8fb6ea6").loc("Velg en annen fil")}
          />
        </div>
      );
    };

    return {
      store: store,
      id: "File",
      getLabel: () => this.L.id("a2dbc22f0f81").loc("Valgt fil"),
      hasUnsavedChanges: null,
      onRenderComponent,
    };
  });

  @observable documentNameField = cveField(() => {
    const userInfo = this.userInfo;
    const _this = this;

    const isAllowedCharacter = (char: string) => {
      if (char === "(") return true;
      if (char === ")") return true;
      if (RegExp(/\n/).test(char)) return false;

      if (RegExp(/\d/).test(char)) return true;
      if (RegExp(/\s/).test(char)) return true;

      try {
        return RegExp(/^\p{L}/, "u").test(char);
      } catch (e) {
        //ie11 (no ES6)
        return (
          char.toUpperCase() != char.toLowerCase() || char.codePointAt(0) > 127
        );
      }
    };

    const store = cveFieldStore({
      value: { filename: "", type: CVEDocumentWords.custom },

      searchString: "",
      newOptionsTrigger: 0,

      get matchesReservedWord() {
        const filename = store.value.filename.toLowerCase();

        return Object.values(CVEDocumentWords).find((word) => {
          if (word === CVEDocumentWords.custom) return false;

          return word.toLowerCase() === filename;
        });
      },

      get validationError(): string {
        if (
          store.value.type === CVEDocumentWords.custom &&
          !store.value.filename.trim()
        ) {
          return this.L.id("f6a538b5f7b4").loc("Dette feltet er påkrevd");
        }

        if (
          store.value.type === CVEDocumentWords.custom &&
          store.matchesReservedWord
        ) {
          const translatedReservedWord = getCVEDocumentWordsTranslation(
            this.L,
            store.matchesReservedWord
          );

          return this.L.id("7b635c1163e7").split(
            `Huk av "{0}" valget for å laste opp fil med dette navnet`,
            [translatedReservedWord]
          );
        }

        const invalidCharacters = _.uniq(
          [...store.value.filename].filter((c) => !isAllowedCharacter(c))
        );

        if (invalidCharacters.length > 0) {
          return this.L.id("71e773adf24a").split(
            "Navnet kan ikke inneholded følgende tegn: {0}",
            [invalidCharacters.join("")]
          );
        }

        return "";
      },

      get searchStringResolved() {
        return store.searchString.toLowerCase();
      },

      get showValidationError(): boolean {
        return store.onChangeCount > 0 || _this.hasAttemptedToClose;
      },
    });

    this.dt.disposers.push(
      autorun(() => {
        const file = this._file;

        if (!file) return;

        const split = file.name.split(".");
        const nameWithoutExtension =
          split.length === 1
            ? split[0]
            : split.slice(0, split.length - 1).join(".");

        store.value.filename = nameWithoutExtension;
      })
    );

    return {
      store,
      id: "DocumentName",
      getLabel: () => null,
      hasUnsavedChanges: null,
      onRenderComponent: observer<OnRenderCvEditComponent>(({ def }) => {
        const file = this._file;

        const split = file.name.split(".");
        const fileExtension = split.length > 1 ? _.last(split) : "";

        return (
          <>
            <div className="p-field-radiobutton">
              <RadioButton
                inputId="CV"
                name="CV"
                className="cve-document-type-radio-button"
                onChange={action((evt) => {
                  store.value.type = CVEDocumentWords.CV;
                })}
                checked={store.value.type === CVEDocumentWords.CV}
              />
              <label htmlFor="CV">
                {getCVEDocumentWordsTranslation(this.L, CVEDocumentWords.CV)}
              </label>
            </div>
            <div className="p-field-radiobutton">
              <RadioButton
                inputId="copyOfId"
                name="copyOfId"
                className="cve-document-type-radio-button"
                onChange={action((evt) => {
                  store.value.type = CVEDocumentWords.copyOfId;
                })}
                checked={store.value.type === CVEDocumentWords.copyOfId}
              />
              <label htmlFor="copyOfId">
                {getCVEDocumentWordsTranslation(
                  this.L,
                  CVEDocumentWords.copyOfId
                )}
              </label>
            </div>
            <div className="p-field-radiobutton">
              <RadioButton
                inputId="signedWorkAgreement"
                name="signedWorkAgreement"
                className="cve-document-type-radio-button"
                onChange={action((evt) => {
                  store.value.type = CVEDocumentWords.signedWorkAgreement;
                })}
                checked={
                  store.value.type === CVEDocumentWords.signedWorkAgreement
                }
              />
              <label htmlFor="signedWorkAgreement">
                {getCVEDocumentWordsTranslation(
                  this.L,
                  CVEDocumentWords.signedWorkAgreement
                )}
              </label>
            </div>
            <div className="p-field-radiobutton">
              <RadioButton
                inputId="custom"
                name="custom"
                className="cve-document-type-radio-button"
                onChange={action((evt) => {
                  store.value.type = CVEDocumentWords.custom;
                })}
                checked={store.value.type === CVEDocumentWords.custom}
              />
              <label htmlFor="custom">
                {getCVEDocumentWordsTranslation(
                  this.L,
                  CVEDocumentWords.custom
                )}
              </label>
            </div>

            {store.value.type === CVEDocumentWords.custom && (
              <div className="p-inputgroup">
                <InputText
                  className={concatClassnames([
                    store.visibleValidationError && "p-invalid",
                    "cve-document-name",
                  ])}
                  id={def.id}
                  name={def.id}
                  placeholder={this.L.id("886a1f045fba").loc(
                    "Gi dokumentet et navn"
                  )}
                  value={store.value.filename}
                  onChange={action((evt) => {
                    store.value.filename = evt.target.value;
                    store.onChangeCount++;
                  })}
                />
                <span className="p-inputgroup-addon">{fileExtension}</span>
              </div>
            )}
          </>
        );
      }),
    };
  });
}

export enum CVEDocumentWords {
  custom = "Annet",
  CV = "CV",
  copyOfId = "Kopi av gyldig ID",
  signedWorkAgreement = "Signert arbeidsavtale",
}

export const getCVEDocumentWordsTranslation = (
  L: LocStoreInstance,
  word: CVEDocumentWords
) => {
  return L.id(`CVEDocumentWordsTranslation_${word}`).loc(word);
};
